import { makeAutoObservable, runInAction } from 'mobx'
import {
  type CreateProductionRouteSheetPayload,
  type ProductionMarkingBatch,
  type ProductionMarkingInventoryProduct,
  type ProductionRouteSheet,
  ProductionService,
  type UpdateProductionRouteSheetPayload,
} from '~/shared/api/services/production'
import type { ProductionCatalogVariation } from '~/shared/api/services/production/types'
import {
  CUSTOM_ORDER_VARIATION_ID,
  CUSTOM_ORDER_VARIATION_NAME,
} from './constants'
import type {
  RouteSheet,
  RouteSheetFormData,
  RouteSheetFormOption,
} from './types'

const EMPTY_ROUTE_SHEET_FORM: RouteSheetFormData = {
  productAvailabilityId: '',
  productVariationId: '',
  quantity: '',
}

const toRouteSheet = (routeSheet: ProductionRouteSheet): RouteSheet => ({
  id: routeSheet.id,
  number: String(routeSheet.number),
  date: routeSheet.launchedAt ?? routeSheet.createdAt ?? null,
  productId: routeSheet.productId,
  productAvailabilityId: routeSheet.productAvailabilityId,
  productName: routeSheet.productName,
  productType: routeSheet.productType ?? '',
  variationId: routeSheet.productVariationId,
  variationName: routeSheet.variationName,
  isCustomOrder: routeSheet.isCustomOrder,
  quantity: routeSheet.quantity,
  totalPrice: routeSheet.totalPrice ?? null,
  canEdit: routeSheet.canEdit,
  status: routeSheet.status,
  employeeId: routeSheet.employeeId,
  currentAssignment: routeSheet.currentAssignment ?? null,
  items: routeSheet.items,
})

export interface SafeMarkingImportError {
  lineNumber: number
  reason: string
}

const mergeById = <T extends { id: string }>(current: T[], incoming: T[]) => {
  const items = new Map(current.map((item) => [item.id, item]))
  incoming.forEach((item) => items.set(item.id, item))
  return Array.from(items.values())
}

const mergeMarkingInventory = (
  current: ProductionMarkingInventoryProduct[],
  incoming: ProductionMarkingInventoryProduct[],
) => {
  const products = new Map(current.map((item) => [item.productId, item]))
  incoming.forEach((item) => products.set(item.productId, item))
  return Array.from(products.values())
}

export class ProductionStore {
  routeSheetForm: RouteSheetFormData = { ...EMPTY_ROUTE_SHEET_FORM }

  routeSheetFormProducts: RouteSheetFormOption[] = []

  routeSheetFormProductsQuery = ''

  routeSheetFormProductsOffset = 0

  routeSheetFormProductsTotal = 0

  routeSheetFormVariations: RouteSheetFormOption[] = []

  routeSheetFormSelectedProduct: RouteSheetFormOption | null = null

  markingBatches: ProductionMarkingBatch[] = []

  markingInventory: ProductionMarkingInventoryProduct[] = []

  markingInventoryOffset = 0

  markingInventoryTotal = 0

  markingInventoryQuery = ''

  isMarkingInventoryLoaded = false

  isMarkingInventoryAllLoaded = false

  private markingInventoryRequestId = 0

  private routeSheetFormProductsRequestId = 0

  private routeSheetFormVariationsRequestId = 0

  loaders = {
    markingBatches: false,
    markingInventory: false,
    loadMoreMarkingInventory: false,
    routeSheetFormProducts: false,
    routeSheetFormVariations: false,
  }

  pendingAction: string | null = null

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  initializeRouteSheetForm = async (sheet?: RouteSheet) => {
    this.resetRouteSheetForm()

    if (!sheet) {
      await this.loadRouteSheetFormProducts()
      return
    }

    this.routeSheetForm = {
      productAvailabilityId: sheet.productAvailabilityId ?? '',
      productVariationId: sheet.isCustomOrder
        ? CUSTOM_ORDER_VARIATION_ID
        : (sheet.variationId ?? ''),
      quantity: String(sheet.quantity),
    }
    this.routeSheetFormSelectedProduct = sheet.productAvailabilityId
      ? {
          id: sheet.productAvailabilityId,
          name: sheet.productName,
          productId: sheet.productId,
        }
      : null

    await Promise.all([
      this.loadRouteSheetFormProducts(),
      sheet.productAvailabilityId
        ? this.loadRouteSheetFormVariations(
            sheet.productAvailabilityId,
            sheet.productName,
            sheet.isCustomOrder
              ? CUSTOM_ORDER_VARIATION_ID
              : (sheet.variationId ?? ''),
          )
        : Promise.resolve(),
    ])
  }

  resetRouteSheetForm = () => {
    this.routeSheetFormProductsRequestId += 1
    this.routeSheetFormVariationsRequestId += 1
    this.routeSheetForm = { ...EMPTY_ROUTE_SHEET_FORM }
    this.routeSheetFormProducts = []
    this.routeSheetFormProductsQuery = ''
    this.routeSheetFormProductsOffset = 0
    this.routeSheetFormProductsTotal = 0
    this.routeSheetFormVariations = []
    this.routeSheetFormSelectedProduct = null
    this.loaders.routeSheetFormProducts = false
    this.loaders.routeSheetFormVariations = false
  }

  clearRouteSheetFormProducts = () => {
    this.routeSheetFormProductsRequestId += 1
    this.routeSheetFormProducts = []
    this.routeSheetFormProductsQuery = ''
    this.routeSheetFormProductsOffset = 0
    this.routeSheetFormProductsTotal = 0
    this.loaders.routeSheetFormProducts = false
  }

  loadRouteSheetFormProducts = async (
    query = this.routeSheetFormProductsQuery,
    action: 'reset' | 'loadMore' = 'reset',
  ) => {
    if (
      action === 'loadMore' &&
      (this.loaders.routeSheetFormProducts ||
        this.routeSheetFormProductsOffset >= this.routeSheetFormProductsTotal)
    ) {
      return
    }

    if (action === 'reset') {
      this.routeSheetFormProductsRequestId += 1
      this.routeSheetFormProducts = []
      this.routeSheetFormProductsQuery = query
      this.routeSheetFormProductsOffset = 0
      this.routeSheetFormProductsTotal = 0
    }
    const requestId = this.routeSheetFormProductsRequestId
    const offset = action === 'reset' ? 0 : this.routeSheetFormProductsOffset
    this.loaders.routeSheetFormProducts = true

    try {
      const response = await ProductionService.getCatalogProducts({
        q: this.routeSheetFormProductsQuery.trim() || undefined,
        limit: 20,
        sort: 'newest',
        offset,
      })

      if (requestId !== this.routeSheetFormProductsRequestId) return

      runInAction(() => {
        this.routeSheetFormProducts =
          action === 'loadMore'
            ? mergeById(this.routeSheetFormProducts, response.data)
            : response.data
        this.routeSheetFormProductsOffset = offset + response.data.length
        this.routeSheetFormProductsTotal = response.total
      })
    } catch (error) {
      if (requestId === this.routeSheetFormProductsRequestId) throw error
    } finally {
      if (requestId === this.routeSheetFormProductsRequestId) {
        runInAction(() => {
          this.loaders.routeSheetFormProducts = false
        })
      }
    }
  }

  loadMoreRouteSheetFormProducts = async () =>
    this.loadRouteSheetFormProducts(
      this.routeSheetFormProductsQuery,
      'loadMore',
    )

  loadRouteSheetFormVariations = async (
    productAvailabilityId: string,
    productName: string,
    productVariationId = '',
  ) => {
    this.routeSheetFormVariationsRequestId += 1
    const requestId = this.routeSheetFormVariationsRequestId
    this.routeSheetForm = {
      ...this.routeSheetForm,
      productAvailabilityId,
      productVariationId,
    }
    this.routeSheetFormVariations = []

    if (!productAvailabilityId) {
      this.loaders.routeSheetFormVariations = false
      return
    }

    this.loaders.routeSheetFormVariations = true
    try {
      const loadCatalogPage = async (
        offset: number,
      ): Promise<ProductionCatalogVariation[]> => {
        const response = await ProductionService.getCatalogVariations({
          q: productName,
          limit: 100,
          sort: 'newest',
          offset,
        })

        if (requestId !== this.routeSheetFormVariationsRequestId) return []

        if (
          response.data.length === 0 ||
          offset + response.data.length >= response.total
        ) {
          return response.data
        }

        return [
          ...response.data,
          ...(await loadCatalogPage(offset + response.data.length)),
        ]
      }
      const catalogVariations = await loadCatalogPage(0)

      if (requestId !== this.routeSheetFormVariationsRequestId) return

      runInAction(() => {
        this.routeSheetFormVariations = [
          ...catalogVariations
            .filter(
              (variation) =>
                variation.productAvailabilityId === productAvailabilityId,
            )
            .map((variation) => ({
              id: variation.id,
              name: variation.variationName,
            })),
          {
            id: CUSTOM_ORDER_VARIATION_ID,
            name: CUSTOM_ORDER_VARIATION_NAME,
          },
        ]
      })
    } catch (error) {
      if (requestId === this.routeSheetFormVariationsRequestId) throw error
    } finally {
      if (requestId === this.routeSheetFormVariationsRequestId) {
        runInAction(() => {
          this.loaders.routeSheetFormVariations = false
        })
      }
    }
  }

  selectRouteSheetFormProduct = async (
    product: RouteSheetFormOption | null,
  ) => {
    this.routeSheetFormSelectedProduct = product
    await this.loadRouteSheetFormVariations(
      product?.id ?? '',
      product?.name ?? '',
    )
  }

  setRouteSheetFormVariation = (productVariationId: string) => {
    this.routeSheetForm.productVariationId = productVariationId
  }

  setRouteSheetFormQuantity = (quantity: string) => {
    this.routeSheetForm.quantity = quantity
  }

  createRouteSheet = async (payload: CreateProductionRouteSheetPayload) => {
    this.pendingAction = 'create-route-sheet'

    try {
      const response = await ProductionService.createRouteSheet(payload)
      return toRouteSheet(response.data.routeSheet)
    } finally {
      runInAction(() => {
        this.pendingAction = null
      })
    }
  }

  updateRouteSheet = async (
    routeSheetId: string,
    payload: UpdateProductionRouteSheetPayload,
  ) => {
    this.pendingAction = `update-route-sheet:${routeSheetId}`

    try {
      const response = await ProductionService.updateRouteSheet(
        routeSheetId,
        payload,
      )
      return toRouteSheet(response.data.routeSheet)
    } finally {
      runInAction(() => {
        this.pendingAction = null
      })
    }
  }

  sendToQualityControl = async (routeSheetId: string) => {
    this.pendingAction = `quality-control-send:${routeSheetId}`

    try {
      const response =
        await ProductionService.sendRouteSheetToQualityControl(routeSheetId)
      return response.data.routeSheet
    } finally {
      runInAction(() => {
        this.pendingAction = null
      })
    }
  }

  loadMarkingBatches = async () => {
    this.loaders.markingBatches = true

    try {
      const response = await ProductionService.getMarkingBatches({
        limit: 100,
        page: 1,
      })

      runInAction(() => {
        this.markingBatches = response.data
      })
      return response.data
    } finally {
      runInAction(() => {
        this.loaders.markingBatches = false
      })
    }
  }

  loadMarkingInventory = async (
    action: 'reset' | 'loadMore' = 'reset',
    query = this.markingInventoryQuery,
  ) => {
    if (action === 'loadMore' && this.isMarkingInventoryAllLoaded) return []

    const loader =
      action === 'loadMore' ? 'loadMoreMarkingInventory' : 'markingInventory'
    if (action === 'reset') {
      this.markingInventoryRequestId += 1
      this.markingInventory = []
      this.markingInventoryOffset = 0
      this.markingInventoryTotal = 0
      this.markingInventoryQuery = query
      this.isMarkingInventoryLoaded = false
      this.isMarkingInventoryAllLoaded = false
      this.loaders.loadMoreMarkingInventory = false
    }

    const requestId = this.markingInventoryRequestId
    const offset = action === 'reset' ? 0 : this.markingInventoryOffset
    this.loaders[loader] = true

    try {
      const response = await ProductionService.getMarkingInventory({
        q: query.trim() || undefined,
        limit: 20,
        sort: 'newest',
        offset,
      })

      if (requestId !== this.markingInventoryRequestId) return []

      runInAction(() => {
        this.markingInventory =
          action === 'loadMore'
            ? mergeMarkingInventory(this.markingInventory, response.data)
            : response.data
        this.markingInventoryOffset = offset + response.data.length
        this.markingInventoryTotal = response.total
        this.markingInventoryQuery = query
        this.isMarkingInventoryLoaded = true
        this.isMarkingInventoryAllLoaded =
          this.markingInventoryOffset >= response.total
      })

      return response.data
    } finally {
      if (requestId === this.markingInventoryRequestId) {
        runInAction(() => {
          this.loaders[loader] = false
        })
      }
    }
  }

  createMarkingBatch = async (productVariationId: string, file: File) => {
    this.pendingAction = 'create-marking-batch'

    try {
      const response = await ProductionService.createMarkingBatch({
        productVariationId,
        file,
      })

      runInAction(() => {
        this.markingBatches = mergeById(this.markingBatches, [
          response.data.batch,
        ])
      })
      return response.data.batch
    } finally {
      runInAction(() => {
        this.pendingAction = null
      })
    }
  }

  archiveMarkingBatch = async (batchId: string) => {
    this.pendingAction = `archive-marking-batch:${batchId}`

    try {
      const response = await ProductionService.archiveMarkingBatch(batchId)

      runInAction(() => {
        this.markingBatches = mergeById(this.markingBatches, [
          response.data.batch,
        ])
      })
      return response.data.batch
    } finally {
      runInAction(() => {
        this.pendingAction = null
      })
    }
  }

  static loadMarkingBatchErrors = async (
    batchId: string,
  ): Promise<SafeMarkingImportError[]> => {
    const response = await ProductionService.getMarkingBatchErrors(batchId, {
      limit: 100,
      page: 1,
    })

    return response.data.map(({ lineNumber, reason }) => ({
      lineNumber,
      reason,
    }))
  }
}
